// safety/incidents-and-injuries/components/tabs/NotesTab.tsx
import React, { useState } from 'react';
import { Mail, FileText, MessageSquare, Trash2 } from 'lucide-react';
import { SectionTitle, AddButton, FormModal } from '../shared';
import { fmtDate, todayISO } from '../../utils/incidents-utils';
import type { Incident, FileNote, FileNoteKind } from '../../../../../types/incident.types';

const KIND_ICON: Record<FileNoteKind, React.ComponentType<{ size?: number }>> = { mail: Mail, doc: FileText, msg: MessageSquare };

export const NotesTab: React.FC<{
    incident: Incident;
    onAdd: (n: FileNote) => void;
    onDelete: (id: string) => void;
}> = ({ incident: r, onAdd, onDelete }) => {
    const [modal, setModal] = useState(false);

    return (
        <>
            <SectionTitle title="File Notes" right={<AddButton label="Add Note" onClick={() => setModal(true)} />} />
            <div className="space-y-3">
                {r.notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
                {r.notes.map((n) => {
                    const Icon = KIND_ICON[n.kind] ?? FileText;
                    return (
                        <div key={n.id} className="rounded-lg border border-border p-4">
                            <div className="flex items-start gap-3">
                                <div className="h-9 w-9 rounded-lg bg-accent flex items-center justify-center text-primary flex-shrink-0">
                                    <Icon size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm font-medium">{n.title}</div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground">{fmtDate(n.date)}</span>
                                            <button onClick={() => onDelete(n.id)} className="text-muted-foreground hover:text-destructive">
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        From: {n.from} → To: {n.to}
                                    </div>
                                    <p className="text-sm text-foreground mt-2">{n.body}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {modal && (
                <FormModal
                    title="Add Note"
                    fields={[
                        { key: 'title', label: 'Title', value: '' },
                        { key: 'kind', label: 'Type', type: 'select', options: ['mail', 'doc', 'msg'], value: 'mail' },
                        { key: 'from', label: 'From', value: '' },
                        { key: 'to', label: 'To', value: r.employeeName },
                        { key: 'body', label: 'Note', type: 'textarea', value: '' },
                    ]}
                    onClose={() => setModal(false)}
                    onSubmit={(v) => {
                        onAdd({ ...(v as any), date: todayISO() });
                        setModal(false);
                    }}
                />
            )}
        </>
    );
};
